import { useCallback } from 'react';
import { getInlineString, getText } from 'i18n/intl';
import { errorNotification, successNotification } from 'utils/notifications';
import { Button } from '@timeedit/ui-components';
import { Popconfirm } from '@timeedit/ui-components/antd';
import type { JobObjectSelection } from '../hooks/useJobSubmit';
import { useStartDeleteJobMutation } from './JobResults/api/objectJobApi';

type Props = {
  selectedObjectsCount: number;
  disabled: boolean;
  jobObjectSelection: JobObjectSelection;
};

const BulkDeleteObjectsJobButton: React.FC<Props> = ({
  disabled,
  selectedObjectsCount,
  jobObjectSelection,
}: Props) => {
  const [startDeleteJob, { isLoading }] = useStartDeleteJobMutation();

  const handleConfirm = useCallback(async () => {
    if (!('filter' in jobObjectSelection)) {
      return;
    }

    try {
      await startDeleteJob({ filter: jobObjectSelection.filter }).unwrap();
      successNotification(getText('object_manager.bulk-delete.success'));
    } catch (error: any) {
      const errorTitle =
        error?.data?.message ?? getText('object_manager.bulk-delete.error');
      errorNotification(errorTitle);
    }
  }, [jobObjectSelection, startDeleteJob]);

  return (
    <Popconfirm
      title={getInlineString(
        'object_manager.bulk-delete.confirm-title',
        selectedObjectsCount
      )}
      description={getText('object_manager.bulk-delete.confirm-description')}
      okText={getText('object_manager.bulk-delete.confirm-ok')}
      cancelText={getText('cancel')}
      okButtonProps={{ danger: true, loading: isLoading }}
      onConfirm={handleConfirm}
      disabled={disabled}
      placement="top"
    >
      <Button danger type="default" size="small" disabled={disabled}>
        {getText('object_manager.bulk-delete.button')}
      </Button>
    </Popconfirm>
  );
};

export default BulkDeleteObjectsJobButton;
